import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FEATURES } from '../../constants';
import { DataOptions, GeoLevelData, MapGeoLevel, TimeRangeMap } from '../../types';

type Props = {
    geoLevel: MapGeoLevel;
    vertical?: boolean;
    dataOptions: DataOptions;
    timeRange: TimeRangeMap;
    geoLevels: GeoLevelData[];
    onSetGeoLevel: (geoLevel: MapGeoLevel) => void;
};

export function GeoLevelPicker({
    geoLevel,
    vertical = false,
    dataOptions,
    timeRange,
    geoLevels,
    onSetGeoLevel,
}: Props) {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);

    const enabledLevels: (Omit<GeoLevelData, 'id'> & { id: MapGeoLevel })[] = [
        ...(FEATURES.LAN_PICKER ? [{ id: 'lan' as const, name: 'Region' }] : []),
        ...geoLevels,
    ];

    function handleKeyDown(e: React.KeyboardEvent, currentIndex: number) {
        const tabs = Array.from(
            containerRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])') ?? []
        );
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            tabs[(currentIndex + 1) % tabs.length]?.focus();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            tabs[(currentIndex - 1 + tabs.length) % tabs.length]?.focus();
        }
    }

    return (
        <div
            ref={containerRef}
            role="tablist"
            aria-label={t('valj-geografisk-indelning') as string}
            className={`tabs ${vertical ? 'tabs-xs' : 'tabs-sm'} tabs-boxed ${
                vertical && 'tooltip tooltip-right'
            }`}
            data-tip={t('valj-geografisk-indelning')}
        >
            {enabledLevels
                .map((level, index) => {
                    const disabled = level.id === 'lan'
                        ? false
                        : Object.values(dataOptions.indicators).reduce((acc, curr) => {
                              if (!curr) return acc;
                              return acc || !timeRange[curr.id][level.id];
                          }, false);
                    return (
                        <button
                            key={level.id}
                            role="tab"
                            aria-selected={geoLevel === level.id}
                            aria-disabled={disabled}
                            disabled={disabled}
                            className={`
                                tab
                                 ${geoLevel === level.id && 'tab-active'}
                                 ${
                                     disabled
                                         ? 'opacity-50 cursor-not-allowed line-through tab-disabled'
                                         : ''
                                 }
                                `}
                            style={{
                                color:
                                    level.loggedIn && geoLevel !== level.id
                                        ? 'oklch(var(--wa))'
                                        : '',
                            }}
                            onClick={() => {
                                onSetGeoLevel(level.id);
                            }}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                        >
                            {vertical ? level.name.charAt(0) : level.name}
                        </button>
                    );
                })}
        </div>
    );
}
